import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const SKEW_MS = 60_000;

const Auth = createContext({
  token: null,
  role: 'viewer',
  user: null,
  loadingUser: true,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
});

/**
 * AuthProvider manages authentication state (JWT, user, role) and token lifecycle.
 * It wires axios with auth headers, handles refreshing the token, and gives access to
 * login/logout through context.
 * 
 * @param {{ children: React.ReactNode}} props 
 * @returns {JSX.Element}
 */
export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [role, setRole] = useState('viewer');
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const refreshWaitersRef = useRef([]);

  useEffect(() => {
    if (API_BASE) {
        axios.defaults.baseURL = API_BASE;
    }
    axios.defaults.withCredentials = true;
  }, []);

  /**
   * Clears any scheduled refresh timer, so it doesn't run unexpectedly.
   * Called before scheduling a new refresh or when logging out.
   */
  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  /**
   * Decodes a JWT, extracts user identity and role, and updates local state.
   * This does not store the token in localStorage or schedule refresh on its own.
   * 
   * @param {string} jwt
   */
  const decodeAndApply = useCallback((jwt) => {
    const p = jwtDecode(jwt);
    setRole(p.role || 'viewer');
    setUser({ id: p.sub, email: p.email });
    setToken(jwt);
  }, []);

  /**
   * Schedules a token refresh shortly before the given JWT expires.
   * If refresh succeeds, then it updates storage and state. 
   * Otherwise, it logs the user out.
   * 
   * @param {string} jwt
   */
  const scheduleProactiveRefresh = useCallback(
    (jwt) => {
        clearRefreshTimer();
        try {
            const { exp } = jwtDecode(jwt);
            const ms = exp * 1000 - Date.now() - SKEW_MS;

            const run = async () => {
                try {
                    const resp = await axios.post('/api/auth/refresh');
                    const newTok = resp?.data?.accessToken;
                    if (newTok) {
                        localStorage.setItem('accessToken', newTok);
                        decodeAndApply(newTok);
                        scheduleProactiveRefresh(newTok);
                    } else {
                        doLogout();
                    }
                } catch {
                    doLogout();
                }
            };

            if (ms > 0) {
                refreshTimerRef.current = setTimeout(run, ms);
            } else {
                run();
            }
        } catch {
            doLogout();
        }
    },
    [decodeAndApply]
  );

  /**
   * Saves the given JWT or clears it, updates auth state, and wires proactive refresh.
   * Passing an incorrect token clears the session, resets user/role, and removes localStorage entry.
   * 
   * @param {string | null} jwt
   */
  const saveToken = useCallback(
    (jwt) => {
      clearRefreshTimer();
      if (jwt) {
        localStorage.setItem('accessToken', jwt);
        decodeAndApply(jwt);
        scheduleProactiveRefresh(jwt);
      } else {
        localStorage.removeItem('accessToken');
        setToken(null);
        setRole('viewer');
        setUser(null);
      }
    },
    [decodeAndApply, scheduleProactiveRefresh]
  );

  /**
   * Logs the user out by calling the backend logout endpoint and clearing local auth state.
   * Any refresh timers are cancelled and stored access token is removed.
   * 
   * @returns {Promise<void>}
   */
  const doLogout = useCallback(async () => {
    clearRefreshTimer();
    try {
        await axios.post('/api/auth/logout');
    } catch {}
    saveToken(null);
  }, [saveToken]);

  /**
   * Handles a successful login by storing the JWT and adding refresh behavior.
   * This is typically called from the login screen after receiving an access token.
   * 
   * @param {string} jwt
   */
  const login = useCallback((jwt) => {
    saveToken(jwt);
  }, [saveToken]);

  const logout = useCallback(() => {
    doLogout();
  }, [doLogout]);

  useEffect(() => {
    (async () => {
        const stored = localStorage.getItem('accessToken');
        if (stored) {
            try {
                const { exp } = jwtDecode(stored);
                if (Date.now() < exp * 1000) {
                    decodeAndApply(stored);
                    scheduleProactiveRefresh(stored);
                    setLoadingUser(false);
                    return;
                }
            } catch {}
        }
        try {
            const resp = await axios.post('/api/auth/refresh');
            const newTok = resp?.data?.accessToken;
            if (newTok) {
                localStorage.setItem('accessToken', newTok);
                decodeAndApply(newTok);
                scheduleProactiveRefresh(newTok);
            } 
        } catch {}
        setLoadingUser(false);
    })();
    return () => clearRefreshTimer();
  }, [decodeAndApply, scheduleProactiveRefresh]);

  useEffect(() => {
    const reqId = axios.interceptors.request.use((cfg) => {
      if (token) {
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${token}`;
      }
      return cfg;
    });

    /**
     * Resolves all queued requests waiting on a refresh with the new token (or failure).
     * Each waiter receives the refreshed token (or null) so it can retry or fail accordingly.
     * 
     * @param {string | null} newTok 
     */
    const notifyWaiters = (newTok) => {
      const q = refreshWaitersRef.current;
      refreshWaitersRef.current = [];
      q.forEach((cb) => cb(newTok));
    };

    const resId = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const original = error?.config || {};
        const status = error?.response?.status;
        const url = (original.url || '').toString();

        const isAuthEndpoint =
          url.includes('/api/auth/login') ||
          url.includes('/api/auth/refresh') ||
          url.includes('/api/auth/logout');

        if ((status === 401 || status === 403) && !original._retry && !isAuthEndpoint) {
          original._retry = true;

          if (isRefreshingRef.current) {
            return new Promise((resolve, reject) => {
              refreshWaitersRef.current.push((newTok) => {
                if (newTok) {
                  original.headers = original.headers || {};
                  original.headers.Authorization = `Bearer ${newTok}`;
                  resolve(axios(original));
                } else {
                  reject(error);
                }
              });
            });
          }

          isRefreshingRef.current = true;
          try {
            const resp = await axios.post('/api/auth/refresh');
            const newTok = resp?.data?.accessToken;
            isRefreshingRef.current = false;

            if (newTok) {
              localStorage.setItem('accessToken', newTok);
              decodeAndApply(newTok);
              scheduleProactiveRefresh(newTok);
              notifyWaiters(newTok);

              original.headers = original.headers || {};
              original.headers.Authorization = `Bearer ${newTok}`;
              return axios(original);
            }
          } catch {
            isRefreshingRef.current = false;
            notifyWaiters(null);
          }

          await doLogout();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, [token, decodeAndApply, scheduleProactiveRefresh, doLogout]);

  const value = useMemo(
    () => ({
      token,
      role,
      user,
      loadingUser,
      isAuthenticated: !!token,
      login,
      logout,
    }),
    [token, role, user, loadingUser, login, logout]
  );

  return <Auth.Provider value={value}>{children}</Auth.Provider>;
};

export const useAuth = () => useContext(Auth);
